import axios from "../config/axiosConfig";

/**
 * Obtiene los detalles principales del módem por su ID interno.
 * @param {number|string} id - ID del módem
 * @param {AbortSignal} [signal] - Opcional: Señal para cancelar la petición
 */
export const getModemById = (id, signal) => {
  return axios.get(`/api/equipos-modem/buscar/${id}`, { signal });
};

/**
 * Obtiene el historial de movimientos de un módem basado en su número de SIM.
 * @param {string} numSim - Número de SIM
 * @param {AbortSignal} [signal] - Opcional: Señal para cancelar la petición
 */
export const getHistorialByNumSim = (numSim, signal) => {
  return axios.get(`/api/historial-modem/sim/${numSim}`, { signal });
};

// ============================================================================
// BÚSQUEDAS EN PLATAFORMAS EXTERNAS (TELEMETRÍA)
// Todas esperan un array de strings en 'values' (ej: ['123456789', 'IMEI...'])
// ============================================================================

/**
 * @param {string[]} values - Array de identificadores (SIM, IMEI)
 * @param {AbortSignal} [signal]
 */
export const searchFlespiPrimary = (values, signal) => {
  return axios.post("/api/flespi/dispositivos/busqueda", { values }, { signal });
};

/**
 * @param {string[]} values - Array de identificadores (SIM, IMEI)
 * @param {AbortSignal} [signal]
 */
export const searchFlespiSecondary = (values, signal) => {
  return axios.post("/api/flespi/dispositivos-secondary/busqueda", { values }, { signal });
};

/**
 * @param {string[]} values - Array de identificadores (SIM, IMEI)
 * @param {AbortSignal} [signal]
 */
export const searchWialonUnits = (values, signal) => {
  return axios.post("/api/wialon/unidades/buscar", { values }, { signal });
};

/**
 * @param {string[]} values - Array de identificadores (SIM, IMEI)
 * @param {AbortSignal} [signal]
 */
export const searchLeasecontrolUnits = (values, signal) => {
  return axios.post("/api/leasecontrol/unidades/buscar", { values }, { signal });
};

/**
 * Busca el "endpoint" (SIM + dispositivo) de Emnify por ICCID — para chips de roaming
 * internacional (modelo "Emnify Internacional"), donde el ICCID es el campo "emai".
 * @param {string} iccid
 * @param {AbortSignal} [signal]
 */
export const buscarEmnifyPorIccid = (iccid, signal) => {
  return axios.get("/api/emnify/endpoint", { params: { iccid }, signal });
};

/**
 * Diagnóstico de por qué un dispositivo de flespi no reporta (logs de conexión, últimos mensajes y Emnify).
 * @param {number|string} id - id del dispositivo en flespi
 * @param {{ secundaria?: boolean, cantidad?: number, signal?: AbortSignal }} [opciones] - secundaria = cuenta Flespi 2; cantidad = 50, 100 o 300 mensajes
 */
export const diagnosticarFlespi = (id, { secundaria = false, cantidad, signal } = {}) => {
  return axios.get(`/api/flespi/dispositivos/${encodeURIComponent(id)}/diagnostico`, {
    params: { ...(secundaria ? { cuenta: "secundaria" } : {}), ...(cantidad ? { cantidad } : {}) },
    signal,
  });
};

/**
 * Reinicia un dispositivo de flespi con el comando de reinicio de su modelo (roles admin, sistemas y gerente).
 * enCola: si el equipo no está conectado, el comando se entrega cuando se conecte (vence en 1 h).
 */
export const reiniciarFlespi = (id, { secundaria = false, enCola = false } = {}) => {
  return axios.post(`/api/flespi/dispositivos/${encodeURIComponent(id)}/reiniciar`, { enCola }, {
    params: secundaria ? { cuenta: "secundaria" } : {},
  });
};

/**
 * Diagnóstico de reporte de una unidad de Wialon o LeaseControl (últimos mensajes y causa probable).
 * @param {"wialon"|"leasecontrol"} plataforma
 * @param {number|string} id - id de la unidad
 * @param {{ cantidad?: number, signal?: AbortSignal }} [opciones]
 */
export const diagnosticarUnidadWialon = (plataforma, id, { cantidad, signal } = {}) => {
  return axios.get(`/api/${plataforma}/unidades/${encodeURIComponent(id)}/diagnostico`, { params: cantidad ? { cantidad } : {}, signal });
};